import { useMemo, useState } from 'react';
import { type CardLimitsResponse, type UpdateLimitCardRequest, Frequency } from '@shared/api/rest/cards';
import { CardNewLimitRow } from '@widgets';
import { Select } from '@shared/widgets/select';
import { Button, Modal, Spinner, Text } from '@shared/widgets';

const FREQUENCIES = [
  { value: Frequency.DAILY, label: Frequency.DAILY },
  { value: Frequency.CYCLE, label: Frequency.CYCLE },
  { value: Frequency.YEAR, label: Frequency.YEAR },
];

interface IRequestNewLimitsModal {
  show: boolean;
  onClose: () => void;
  limits: CardLimitsResponse;
  symbol?: string;
  onRequest: (newLimits: UpdateLimitCardRequest) => void;
  loading: boolean;
}

export const RequestNewLimitsModal = (props: IRequestNewLimitsModal) => {
  const [frequency, setFrequency] = useState<Frequency>(props.limits.frequency);
  const currentLimitValues = useMemo(() => {
    if (props.limits.frequency === Frequency.DAILY) return props.limits.dayLimits;
    else if (props.limits.frequency === Frequency.CYCLE) return props.limits.cycleLimits;
    else if (props.limits.frequency === Frequency.YEAR) return props.limits.yearLimits;
    else return props.limits.dayLimits;
  }, [props.limits, frequency]);

  const [newLimits, setNewLimits] = useState<UpdateLimitCardRequest>({
    retailNumber: props.limits.currentRetailNumber,
    retailAmount: props.limits.currentRetailAmount,
    cashAmount: props.limits.currentCashAmount,
    cashNumber: props.limits.currentCashNumber,
    otcAmount: props.limits.currentOTCAmount,
    otcNumber: props.limits.currentOTCNumber,
    frequency: props.limits.frequency,
  });

  const onChange = (part: Partial<UpdateLimitCardRequest>) => {
    setNewLimits({ ...newLimits, ...part });
  };

  const save = async () => {
    await props.onRequest({ ...newLimits, frequency: frequency });
    props.onClose();
  };

  const closeModal = () => {
    setNewLimits({
      retailNumber: props.limits.currentRetailNumber,
      retailAmount: props.limits.currentRetailAmount,
      cashAmount: props.limits.currentCashAmount,
      cashNumber: props.limits.currentCashNumber,
      otcAmount: props.limits.currentOTCAmount,
      otcNumber: props.limits.currentOTCNumber,
      frequency: frequency,
    });
    props.onClose();
  };

  return (
    <Modal isOpen={props.show} onClose={closeModal} title='Request new limits' width='950px'>
      {props.loading ? (
        <Spinner />
      ) : (
        <>
          <div className='w-[10em] mb-5'>
            <Text>Limit frequency</Text>
            <Select items={FREQUENCIES} value={frequency} onChange={(val) => setFrequency(val as Frequency)} />
          </div>
          <div className='flex justify-between -mx-5'>
            <CardNewLimitRow
              limitRowLabel='Retail limit'
              currentAmount={currentLimitValues?.retailAmount || 0}
              limitAmount={props.limits.currentRetailAmount}
              limitNumber={props.limits.currentRetailNumber}
              currentNumber={currentLimitValues?.retailNumber || 0}
              symbol={props.symbol}
              firstInputLabel='New amount'
              firstInputValue={newLimits.retailAmount}
              firstInputOnChange={(value) => onChange({ retailAmount: parseFloat(value) })}
              firstInputLimitValue={newLimits.retailAmount}
              secondInputLabel='New txn number'
              secondInputValue={newLimits.retailNumber}
              secondInputOnChange={(value) => onChange({ retailNumber: parseInt(value) })}
              secondInputLimitValue={newLimits.retailNumber}
            />
            <CardNewLimitRow
              limitRowLabel='Cash limit'
              limitAmount={props.limits.currentCashAmount}
              currentAmount={currentLimitValues?.cashAmount || 0}
              limitNumber={props.limits.currentCashNumber}
              currentNumber={currentLimitValues?.cashNumber || 0}
              symbol={props.symbol}
              firstInputLabel='New amount'
              firstInputValue={newLimits.cashAmount}
              firstInputOnChange={(value) => onChange({ cashAmount: parseFloat(value) })}
              firstInputLimitValue={newLimits.cashAmount}
              secondInputLabel='New txn number'
              secondInputValue={newLimits.cashNumber}
              secondInputOnChange={(value) => onChange({ cashNumber: parseInt(value) })}
              secondInputLimitValue={newLimits.cashNumber}
            />
            <CardNewLimitRow
              limitRowLabel='Total limit'
              limitAmount={props.limits.currentOTCAmount}
              currentAmount={currentLimitValues?.otcAmount || 0}
              limitNumber={props.limits.currentOTCNumber}
              currentNumber={currentLimitValues?.otcNumber || 0}
              symbol={props.symbol}
              firstInputLabel='New amount'
              firstInputValue={newLimits.otcAmount}
              firstInputOnChange={(value) => onChange({ otcAmount: parseFloat(value) })}
              firstInputLimitValue={newLimits.otcAmount}
              secondInputLabel='New txn number'
              secondInputValue={newLimits.otcNumber}
              secondInputOnChange={(value) => onChange({ otcNumber: parseInt(value) })}
              secondInputLimitValue={newLimits.otcNumber}
            />
          </div>
          <div className='text-center'>
            <Button
              color='primary'
              disabled={Number.isNaN(newLimits.retailAmount) || Number.isNaN(newLimits.cashAmount) || Number.isNaN(newLimits.otcAmount)}
              onClick={save}
            >
              Request new limits
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};
